import * as React from 'react';
import {useEffect, useState} from 'react';
import {FormattedMessage, InjectedIntl, InjectedIntlProps, injectIntl} from 'react-intl';
import {Form, Formik, FormikProps} from "formik";
import * as Yup from 'yup';
import viaForSchools from "../../assets/viaForSchools.svg";
import viaLogo from "../../assets/viaLogo.svg";
import {Button} from "@material-ui/core";
import {RouteComponentProps} from "react-router";
import {FormContainer, PageContainer, RowContainer, SeperateLine} from '../common/styles';
import * as H from "history";
import {CognitoUser} from "amazon-cognito-identity-js";
import {Auth} from "aws-amplify";
import {ToastType} from "../../stores/ui-state.store";
import ToastSnackBar from "../common/toastSnackBar";
import handleRedirect, {getRedirectUri} from '../../utils/redirectHelper';
import {logEvent, MParticleEventName} from "../../utils/m-particle-utils";
import {SMS_MFA, SOFTWARE_TOKEN_MFA} from "../../constants/constants";
import getSSOProviders from "../../utils/ssoHelper";
import makeStyles from "@material-ui/core/styles/makeStyles";
import EmailField from "../common/emailField";
import PasswordField from "../common/passwordField";


enum LoginFormFields {
    Email = 'Email',
    Password = 'Password',
}

interface OwnProps {
    setUser: any;
    authClient: any;
    isViaForSchools: boolean;
    queryParams: string;
}

interface FormValues {
    [LoginFormFields.Email]: string;
    [LoginFormFields.Password]: string;
}

const getValidationSchema = (intl: InjectedIntl) => Yup.object().shape({
    [LoginFormFields.Email]: Yup.string().email(intl.messages['login.email.invalid']).required(intl.messages['login.email.required']),
    [LoginFormFields.Password]: Yup.string().required(intl.messages['login.password.required']),
});

const initialValues: FormValues = {
    [LoginFormFields.Email]: '',
    [LoginFormFields.Password]: '',
};

type Props = FormikProps<FormValues> & RouteComponentProps & OwnProps & InjectedIntlProps;

const deleteCognitoCookies = (domain) => {
    let cognitoCookies = document.cookie.split(";")
                                        .filter(item => item.indexOf("CognitoIdentityServiceProvider") !== -1)
                                        .map(item => item.trim())
    for (var i = 0; i < cognitoCookies.length; i++) {
        document.cookie = `${cognitoCookies[i]}=;expires=${new Date(0).toUTCString()};path=/;domain=${domain}`;
    }
};

const signIn = async (username: string, password: string, history: H.History, setUser: (user: CognitoUser | any) => void,
                      setSubmitting: (isSubmitting: boolean) => void,
                      setShowToast: (showToast: boolean) => void,
                      setToastMessage: (toastMessage: string) =>void,
                      setToastType: (toastType: ToastType) => void,
                      authClient: any, queryParams: string) => {
    try {
        deleteCognitoCookies(authClient.userPool.storage.domain);
        const user: CognitoUser | any = await authClient.signIn(username.toLowerCase(), password);
        if (user.challengeName === 'MFA_SETUP') {
            const code = await authClient.setupTOTP(user);
            setUser(user);
            user.email = username.toLowerCase();
            history.push({pathname: '/mfaSetup', search: queryParams,
                state: {code: code, username: user.getUsername()}});
        } else {
            setUser(user);
            user.email = username.toLowerCase();
            if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
                history.push({pathname: '/changePassword', search: queryParams, state: {oldPassword: password}});
            } else if (user.challengeName === SOFTWARE_TOKEN_MFA) {
                history.push({pathname: '/mfaValidation', search: queryParams, state: {challenge: user.challengeName}});
            } else if (user.challengeName === SMS_MFA) {
                let phoneNumber = user.challengeParam ?  user.challengeParam['CODE_DELIVERY_DESTINATION'] : null;
                phoneNumber = phoneNumber.replace('+', '*');
                history.push({pathname: '/mfaValidation', search: queryParams, state: {challenge: user.challengeName, phoneNumber: phoneNumber}});
            } else {
                logEvent(MParticleEventName.LOGIN_SUCCESS, Login.displayName, {username: username,});
                handleRedirect(history, queryParams);
            }
        }
    } catch (error) {
        setToastType(ToastType.ERROR);
        if (error.code === 'UserNotFoundException') {
            setSubmitting(false);

            return authClient.signIn(username, password);
        } else if (error.code === 'NotAuthorizedException') {
            if (error.message === 'Temporary password has expired and must be reset by an administrator.'){
                setToastMessage('login.change-password.tmp-password-expired');
            } else if (error.message === 'Password attempts exceeded') {
                setToastMessage('login.via.password-attempts-exceed');
            }
            else {
                setToastMessage('login.via.user-or-password-incorrect');
            }
        } else if (error.code === 'PasswordResetRequiredException'){
            setUser({email: username.toLowerCase()});
            history.push('/resetPassword');
            return;
        } else {
            setToastMessage('login.via.general-error-message');
        }
        setShowToast(true);
        setSubmitting(false);
        logEvent(MParticleEventName.LOGIN_FAILURE, Login.displayName, {username: username, errorCode: error.code, errorMessage: error.message,});
        throw error;
    }
};

const useStyles = makeStyles({
    forgotPasswordButton: {
        backgroundColor: "white",
        color: '#0b7eb5',
        marginTop: "10px",
        width: "100%",
        fontWeight: "bold",
        '&:hover': {
            backgroundColor: '#0b7eb5',
            color: 'white',
        },
    }});

const Login = (props: Props) => {
    const classes = useStyles();
    const {setUser, history, intl, location,
        authClient, isViaForSchools, queryParams} = props;
    const [showToast, setShowToast] = useState(false);
    const [toastType, setToastType] = useState(ToastType.SUCCESS);
    const [toastMessage, setToastMessage] = useState('login.via.general-error-message');
    const validationSchema = getValidationSchema(intl);

    // @ts-ignore
    const passwordWasSet = location.state && location.state.passwordWasSet;

    useEffect(() => {
        if(passwordWasSet)
        {
            setToastMessage("login.password-set-message");
            setToastType(ToastType.SUCCESS);
            setShowToast(true);
        }
    }, [passwordWasSet]);

    const loginWithFederatedSignIn = (providerName: string) => {
        logEvent(MParticleEventName.SSO_LOGIN, Login.displayName, {ssoProvider: providerName,});
        const redirectPath = getRedirectUri(history, queryParams);
        return Auth.federatedSignIn({customProvider: providerName, customState: redirectPath});
    };

    const renderSSOProviders = () => {
        const ssoProviders = getSSOProviders(queryParams);
        return ssoProviders.map((ssoProvider) =>
            (<React.Fragment>
                <RowContainer style={{margin: "5px 5px", flexDirection: "column"}}>
                                    <span style={{margin: "auto", padding: "10px", color: "#555555"}}>
                                        <FormattedMessage id={ssoProvider.titleStringKey}/>
                                    </span>
                    <Button
                        style={{margin: "5px 65px",
                             backgroundColor: "#20B7E9",
                            color: "white", height: "40px", marginTop: "16px"
                        }}
                        onClick={() => loginWithFederatedSignIn(ssoProvider.providerName)}
                    >
                        <FormattedMessage id={ssoProvider.buttonStringKey}/>
                    </Button>
                </RowContainer>
                < RowContainer style={{padding: "12px 61px", alignItems: "center"}}>
                    <SeperateLine/>
                    <span style={{padding: "0px 7px", color: "#555", fontWeight: 300}}>
                                    <FormattedMessage id='login.or' />
                                    </span>
                    <SeperateLine />
                </RowContainer>
            </React.Fragment>)
        )
    };

    const [passwordShown, setPasswordShown] = useState(false);
    const togglePasswordVisibility = () => {
        setPasswordShown(passwordShown ? false : true);
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={ async (fields, {setSubmitting}) => {
                const email = fields[LoginFormFields.Email];
                const password = fields[LoginFormFields.Password];
                await signIn(email, password, history,
                    setUser, setSubmitting,
                    setShowToast, setToastMessage, setToastType, authClient, queryParams);
                setSubmitting(false);
            }}
            render={() => (
                <PageContainer>
                    <FormContainer>
                    <RowContainer style={{backgroundColor: "#F3F5F8", margin: 0}}>
                    <div style={{padding: "20px", margin: "auto"}}>
                        { isViaForSchools ? <img alt="" src={viaForSchools}/> : <img alt="" src={viaLogo}/>}
                    </div>
                    </RowContainer>
                    <Form style={{ display: 'flex', flexDirection: 'column' }}>
                            {renderSSOProviders()}
                            <RowContainer style={{margin: "5px 70px 70px", flexDirection: "column"}}>
                                <span style={{margin: "auto", padding: "10px", color: "#555"}}>
                                    <FormattedMessage id='login.via.login-message'/>
                                </span>
                                <EmailField name={LoginFormFields.Email}
                                            label={intl.messages['login.login.email-field']}
                                            id={'login.email-field'}/>
                                <PasswordField name={LoginFormFields.Password}
                                               label={intl.messages['login.login.password-field']}
                                               id={'login.password-field'}
                                               passwordShown={passwordShown}
                                               style={{margin: "16px 0px"}}
                                               togglePasswordVisibility={togglePasswordVisibility}/>
                                <Button
                                    type="submit"
                                    data-testid={`login-button`}
                                    style={{width: "100%", marginTop: "5px", height: "40px",
                                        backgroundColor: "#20B7E9", color: "white"}}>
                                    <FormattedMessage id='login.via.login-button' />
                                </Button>
                                <Button
                                    className={classes.forgotPasswordButton}
                                    onClick={() => history.push('/forgotPassword')}>
                                    <FormattedMessage id='login.via.forgot-password-button' />
                                </Button>
                            </RowContainer>
                        </Form>
                    </FormContainer>
                    <ToastSnackBar show={showToast} message={intl.messages[toastMessage]}
                                   toastType={toastType}
                                   onClose={() => {setShowToast(false); }}/>
                </PageContainer>
            )} />
    );
};

Login.displayName = 'Login';
export default injectIntl(Login);
